/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import Select from 'react-select';
import { ErrorMessage } from '../common';
import LoadingSpinner from '../common/LoadingSpinner';
import { QuickActionItem } from '../common/quickActions';
import { executeHttpRequest } from '../../epics/api';
import { getErrorMessages } from '../../utils';
import { TFreelancerData, TPageNotification } from '../../types';


type TCreateListModalProps = {
    isOpen: boolean;
    handleClose: () => void;
    getAvailabilityLists: () => void;
    setSelectedList: (value: number) => void;
}

export function CreateListModal({
    isOpen,
    handleClose,
    getAvailabilityLists,
    setSelectedList,
}: TCreateListModalProps) {
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleCreate = () => {
        setIsLoading(true);
        setError(null);

        executeHttpRequest(
            'availability-lists/',
            'post',
            { name: inputValue },
        )
            .then(response => {
                setIsLoading(false);
                getAvailabilityLists();
                setSelectedList(response.data.content.id);
                handleClose();
            })
            .catch(err => {
                setIsLoading(false);
                setError(getErrorMessages(err));
            });
    };

    return (
        <Modal
            className="add-to-availability-list__create-modal"
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Create Availability List"
            ariaHideApp={false}
        >
            <h4>Create a new availability list</h4>
            <div className="form">
                <p>Please provide the name of the new availability list</p>
                <input
                    type="text"
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    maxLength={30}
                />
                <div className="buttons-container">
                    <button
                        className="btn btn--round-small"
                        type="button"
                        onClick={handleCreate}
                        disabled={isLoading || inputValue === ''}
                    >
                        {isLoading ? (
                            <LoadingSpinner
                                className="mass-booking-spinner"
                                backgroundColor="white"
                            />
                        ) : (
                            'CREATE'
                        )}
                    </button>
                    <button
                        className="btn cancel-btn"
                        type="button"
                        onClick={handleClose}
                    >
                        CANCEL
                    </button>
                </div>
                {error && <ErrorMessage message={error}/>}
            </div>
        </Modal>
    );
}

type TAddToAvailabilityListModalProps = {
    freelancers: TFreelancerData[];
    isOpen: boolean;
    handleClose: () => void;
    notification: TPageNotification;
    onActionSuccess?: (id: string) => void;
    preloadedOptions?: { label: string, value: string | number }[];
}

type TAvailabilityList = {
    id: number;
    name: string;
}

export function AddToAvailabilityListModal({
    freelancers,
    isOpen,
    handleClose,
    notification,
    onActionSuccess = null,
    preloadedOptions = null,
}: TAddToAvailabilityListModalProps) {
    const [options, setOptions] = useState(preloadedOptions);
    const [selectedList, setSelectedList] = useState(null);
    const [fetchListsError, setFetchListsError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isCreateListModalOpen, setIsCreateListModalOpen] = useState(false);

    const getAvailabilityLists = () => {
        setIsLoading(true);
        setFetchListsError(null);

        executeHttpRequest('availability-lists/')
            .then(response => {
                setIsLoading(false);

                const availabilityLists =
                    response.data.content.map((item: TAvailabilityList) => ({
                        label: item.name,
                        value: item.id,
                    }));
                setOptions(availabilityLists);
            })
            .catch(err => {
                setIsLoading(false);
                setFetchListsError(getErrorMessages(err));
            });
    };

    useEffect(() => {
        if (!options) {
            getAvailabilityLists();
        }
    }, []);

    const handleOpenCreateListModal = () => {
        setError(null);
        setIsCreateListModalOpen(true);
    };

    const handleCloseCreateListModal = () => setIsCreateListModalOpen(false);

    const handleSelectList = () => {
        setIsLoading(true);
        setError(null);

        const freelancersIds =
            freelancers.map((freelancer: TFreelancerData) => freelancer.id);

        executeHttpRequest(
            `availability-lists/${selectedList}/add/`,
            'patch',
            { freelancers: freelancersIds },
        )
            .then(() => {
                setIsLoading(false);
                const freelancersCount = freelancersIds.length;
                const listName =
                    options.find(list => list.value === selectedList).label;
                const successMessage = freelancersCount > 1
                    ? `Successfully added ${freelancersCount} workers to ${listName}`
                    : `Successfully added ${freelancers[0].full_name} to ${listName}`;
                notification.success('Success', successMessage);
                if (onActionSuccess) {
                    onActionSuccess(selectedList);
                }
                handleClose();
            })
            .catch(err => {
                setIsLoading(false);
                setError(getErrorMessages(err));
            });
    };

    return (
        <Modal
            className="add-to-availability-list__select-modal"
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Add Worker to Availability List"
            ariaHideApp={false}
        >
            <p className="modal-title">Add to list</p>
            <div className="form">
                <p>Please select an existing availability list or create a new one</p>
                <div className="add-to-availability-list__select-container">
                    <div className="add-to-availability-list__select">
                        <Select
                            value={selectedList}
                            clearable={false}
                            onChange={event => setSelectedList(event.value)}
                            options={options}
                        />
                    </div>
                    <button
                        className="btn btn--round-small"
                        type="button"
                        disabled={!!fetchListsError}
                        onClick={handleOpenCreateListModal}
                    >
                        NEW LIST
                    </button>
                </div>
                <div className="buttons-container">
                    <button
                        className="btn btn--round-small"
                        type="button"
                        disabled={!!fetchListsError || !selectedList || isLoading}
                        onClick={handleSelectList}
                    >
                        {isLoading ? (
                            <LoadingSpinner
                                className="mass-booking-spinner"
                                backgroundColor="white"
                            />
                        ) : (
                            'SELECT'
                        )}
                    </button>
                    <button
                        className="btn cancel-btn"
                        type="button"
                        onClick={handleClose}
                    >
                        CANCEL
                    </button>
                </div>
                {fetchListsError
                    ? <ErrorMessage message={fetchListsError}/>
                    : error
                        ? <ErrorMessage message={error}/>
                        : <></>
                }
                {isCreateListModalOpen && (
                    <CreateListModal
                        isOpen={isCreateListModalOpen}
                        handleClose={handleCloseCreateListModal}
                        getAvailabilityLists={getAvailabilityLists}
                        setSelectedList={setSelectedList}
                    />
                )}
            </div>
        </Modal>
    );
}

type TAddToAvailabilityListProps = {
    freelancers: TFreelancerData[];
    notification: TPageNotification;
}

export default function AddToAvailabilityList({
    freelancers,
    notification,
}: TAddToAvailabilityListProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    return (
        <>
            <QuickActionItem
                label="Add to availability list"
                icon="icon-list-edit"
                onClick={handleOpenModal}
            />
            {isModalOpen && (
                <AddToAvailabilityListModal
                    freelancers={freelancers}
                    isOpen={isModalOpen}
                    handleClose={handleCloseModal}
                    notification={notification}
                />
            )}
        </>
    );
}
